import _ from 'lodash';
import { connect } from 'react-redux';
import React, { useState, useEffect, useCallback } from 'react';
import { Dimmer, WppSpinner } from 'buildingBlocks';
import HighchartsWrapper from 'components/HighchartsWrapper';
import { setOptions, handleDateRangeChange as handleDateRangeChangeAction } from 'containers/StrategyAnalytics/actions';
import Options from 'containers/StrategyAnalytics/components/Options';
import { getDefaultKPIsByStrategy } from 'containers/StrategyAnalytics/utils/metricsUtils';
import {
  defaultChartOptions,
} from 'containers/StrategyAnalytics/constants/chartSeriesConfigs';
import { Strategy, Flight, EnvironmentVariables } from 'utils/types';
import { Metric } from 'containers/StrategyAnalytics/constants/metricsConstants';
import { OptionValue, LeafAnalyticsPerFlight, SegmentGroup, LeafAnalyticsPerFlightDatum } from 'containers/StrategyAnalytics/types';
import { getSegmentIdentifier } from 'containers/StrategyWizard/ConfigurationByStrategyType/segmentUtils';

import { fetchAnalytics, fetchSegments, resetState } from '../actions';
import Table from './SegmentAgeTable';
import { getChartConfig } from '../utils';

type Props = {
  metrics: { [key: string]: Metric }
  strategy: Strategy
  loading: boolean
  noData: boolean
  errorAnalytics: boolean
  onChange: Function
  fetchAnalytics: Function
  fetchSegments: Function
  resetState: Function
  optionValue: OptionValue
  flightSelected: string | null
  segmentSelected: number | null
  segmentGroupSelected: string | null
  flights: Array<Flight>
  analytics: Array<unknown>
  data: LeafAnalyticsPerFlight
  segments: Array<{}>
  segmentGroups: Array<SegmentGroup>
  handleDateRangeChange: Function
  env: EnvironmentVariables
};

const SEGMENT_IDENTIFIER_FIELD = 'externalId';

const getSegmentOptions = (segments) => _.map(segments, (segment) => ({
  key: String(getSegmentIdentifier(segment)),
  value: String(getSegmentIdentifier(segment)),
  text: segment.shortName,
}));

const SegmentRecency = (props: Props) => {
  const [filteredName, setFilteredName] = useState<string | null>(null);
  const [filteredData, setFilteredData] = useState<LeafAnalyticsPerFlight>([]);

  const {
    strategy,
    fetchAnalytics: fetchAnalyticsFromProps,
    fetchSegments: fetchSegmentsFromProps,
    resetState: resetStateFromProps,
    flightSelected,
    segmentSelected,
    segmentGroupSelected,
    data,
    segments,
    flights,
    onChange,
    segmentGroups,
    optionValue,
    loading,
    noData,
    env,
    handleDateRangeChange,
  } = props;

  const updateFilter = useCallback(
    ({ selectedFlight, selectedSegment, selectedSegmentGroup }: { selectedFlight?: string | null, selectedSegment?: number | null, selectedSegmentGroup?: string | null }) => {
      const filter: { flight?: number, segmentId?: number, segmentGroupName?: string } = {};
      const currentFilteredNameParts: string[] = [];
      if (selectedFlight) {
        const flight = _.get(flights, selectedFlight);
        if (flight) {
          filter.flight = flight.id;
          currentFilteredNameParts.push(`Flight: ${flight.name}`);
        }
      }
      if (selectedSegment) {
        filter.segmentId = selectedSegment;
        const foundSegment = _.find(segments, (seg: any) => _.toString(_.get(seg, SEGMENT_IDENTIFIER_FIELD)) === _.toString(selectedSegment));
        if (foundSegment) {
          currentFilteredNameParts.push(`Segment ${foundSegment.shortName}`);
        }
      }
      if (selectedSegmentGroup) {
        filter.segmentGroupName = selectedSegmentGroup;
        currentFilteredNameParts.push(`Segment Group ${selectedSegmentGroup}`);
      }

      const newFilteredName = _.size(currentFilteredNameParts) > 0 ? _.join(currentFilteredNameParts, ' | ') : null;
      setFilteredName(newFilteredName);
      const filteredDataResult = !_.isEmpty(filter) ? _.filter(data, filter) : [];
      const validFilteredData: LeafAnalyticsPerFlight = _.filter(
        filteredDataResult,
        (item): item is LeafAnalyticsPerFlightDatum => typeof item !== 'number' && typeof item !== 'function',
      );
      setFilteredData(validFilteredData);
    },
    [data, segments, flights],
  );

  const onDateRangeChange = useCallback((dateRange: number) => handleDateRangeChange(dateRange), [handleDateRangeChange]);

  const metrics = getDefaultKPIsByStrategy(strategy);

  useEffect(() => {
    fetchAnalyticsFromProps({
      strategy,
    });
    const strategySegmentGroups = _.get(strategy, 'config.segmentGroups', []);
    const allSegments = _.flatten(_.map(strategySegmentGroups, 'segments'));
    if (strategy?.member?.id && strategy?.advertiser?.externalId && !_.isEmpty(allSegments)) {
      fetchSegmentsFromProps({
        memberId: strategy.member.id,
        advertiserId: strategy.advertiser.externalId,
        ids: allSegments,
      });
    }
    return () => {
      resetStateFromProps();
    };
  }, [fetchAnalyticsFromProps, fetchSegmentsFromProps, resetStateFromProps, strategy]);

  useEffect(() => {
    updateFilter({
      selectedFlight: flightSelected,
      selectedSegment: segmentSelected,
      selectedSegmentGroup: segmentGroupSelected,
    });
  }, [flightSelected, segmentSelected, segmentGroupSelected, data, segments, flights, updateFilter]);

  return (
    <Dimmer.Dimmable dimmed={loading}>
      <Dimmer active={loading}>
        <WppSpinner label="Loading data" />
      </Dimmer>
      <Options
        segmentGroups={segmentGroups}
        segments={getSegmentOptions(segments || [])}
        showSegments
        optionValue={optionValue}
        onChange={onChange}
        flights={flights ? _.values(flights) : []}
        onDateRangeChange={onDateRangeChange}
        env={env}
      />
      {
        !loading
        && (
          <div>
            <HighchartsWrapper
              noData={noData}
              defaultChartOptions={defaultChartOptions}
              {...getChartConfig(filteredName, filteredData, optionValue, data, strategy)}
            />
            <Table
              filteredName={filteredName}
              filteredData={filteredData}
              noData={noData}
              loading={loading}
              metrics={_.values(metrics)}
              data={_.isEmpty(filteredName) ? data : filteredData}
            />
          </div>
        )
      }
    </Dimmer.Dimmable>
  );
};

const mapStateToProps = (state) => {
  const strategy = state.strategyAnalytics.metadata.strategy;
  const data = state.strategyAnalytics.segmentRecency.data;
  const segmentGroups = _.map(_.get(strategy, 'config.segmentGroups', []), (sg) => ({
    key: sg.name,
    value: sg.name,
    text: sg.name,
    segments: sg.segments,
  }));

  return {
    strategy,
    analytics: state.strategyAnalytics.metadata.analytics,
    metrics: state.strategyAnalytics.metadata.metrics,
    flights: _.keyBy(state.strategyAnalytics.metadata.flights, 'externalId'),
    flightSelected: state.strategyAnalytics.optionValue.flight,
    segmentSelected: state.strategyAnalytics.optionValue.segment,
    segmentGroupSelected: state.strategyAnalytics.optionValue.segmentGroup,
    segmentGroups,
    data,
    noData: state.strategyAnalytics.segmentRecency.noData,
    loading: !strategy
      || state.strategyAnalytics.segmentRecency.loading
      || state.strategyAnalytics.segmentRecency.loadingSegments,
    segments: state.strategyAnalytics.segmentRecency.segments,
    optionValue: state.strategyAnalytics.optionValue,

    errorAnalytics: state.strategyAnalytics.segmentRecency.error,
  };
};

export default connect(mapStateToProps, {
  onChange: setOptions,
  fetchAnalytics,
  fetchSegments,
  resetState,
  handleDateRangeChange: handleDateRangeChangeAction,
})(SegmentRecency);
